import {
  ExclamationTriangleIcon,
  InfoCircledIcon,
} from '@radix-ui/react-icons';
import { ClassValue } from 'clsx';
import cn from '../../app/utils/cn';
import { Popover } from './Popover';

export type HintVariant = 'default' | 'warning';

interface IHintProps {
  children: React.ReactNode;
  variant?: HintVariant;
  id?: string;
  className?: ClassValue;
}

const variantStyles: Record<HintVariant, string> = {
  default: 'text-text-tertiary',
  warning: 'text-amber-700 dark:text-amber-200',
};

const variantIcons: Record<HintVariant, typeof InfoCircledIcon> = {
  default: InfoCircledIcon,
  warning: ExclamationTriangleIcon,
};

export default function Hint({
  children,
  variant = 'default',
  id,
  className,
}: IHintProps) {
  const Icon = variantIcons[variant];

  return (
    <p
      id={id}
      className={cn(
        'flex items-start gap-1.5 text-xs leading-snug',
        variantStyles[variant],
        className,
      )}
    >
      <Icon className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

interface IHintInfoProps {
  label: string;
  children: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  className?: ClassValue;
}

export function HintInfo({
  label,
  children,
  side = 'top',
  align = 'center',
  className,
}: IHintInfoProps) {
  return (
    <Popover.Root>
      <Popover.Trigger
        aria-label={label}
        className={cn(
          'text-text-tertiary hover:text-text-primary flex size-10 items-center justify-center rounded transition-colors focus-visible:outline-2 focus-visible:outline-green-600 dark:focus-visible:outline-green-400',
          className,
        )}
      >
        <InfoCircledIcon className="size-4" />
      </Popover.Trigger>
      <Popover.Content
        side={side}
        align={align}
        sideOffset={4}
        className="text-text-secondary max-w-64 p-3 text-xs leading-snug"
      >
        {children}
      </Popover.Content>
    </Popover.Root>
  );
}
